/**
 * Base adapter for vendors whose integration has not yet shipped.
 *
 * Subclasses declare their static capability matrix and vendor identity.
 * All DeviceContract methods that require live vendor API calls throw
 * VendorNotIntegratedError — the caller receives a clear, typed signal
 * rather than a generic "not a function" error.
 *
 * Lifecycle state and identity methods work correctly so that HalService
 * can register, route, and inspect unintegrated devices without crashing.
 */

import { VendorNotIntegratedError } from './VendorNotIntegratedError.js';

export class NotImplementedAdapter {
  /**
   * @param {import('../contracts/DeviceContract.ts').DeviceRecord} device
   * @param {string} vendor  VendorId string for this adapter
   * @param {import('../contracts/DeviceContract.ts').CapabilityMatrix} capabilities
   */
  constructor(device, vendor, capabilities) {
    this._device = device ?? {};
    this._vendor = vendor;
    this._capabilities = capabilities;
    this._lifecycleState = this._device.lifecycleState ?? 'provisioned';
  }

  getDeviceId()      { return this._device.id; }
  getVendor()        { return this._vendor; }
  getModel()         { return this._device.model; }
  getCapabilities()  { return this._capabilities; }
  getLifecycleState() { return this._lifecycleState; }

  async setLifecycleState(state) {
    this._lifecycleState = state;
  }

  _notImpl(method) {
    throw new VendorNotIntegratedError(this._vendor, method);
  }

  async pushConfig()        { this._notImpl('pushConfig'); }
  async getCurrentConfig()  { this._notImpl('getCurrentConfig'); }
  async getPendingConfig()  { this._notImpl('getPendingConfig'); }
  async rollbackConfig()    { this._notImpl('rollbackConfig'); }
  async getTelemetry()      { this._notImpl('getTelemetry'); }
  subscribeToTelemetry()    { this._notImpl('subscribeToTelemetry'); }
  async getDeviceHealth()   { this._notImpl('getDeviceHealth'); }
  subscribeToEvents()       { this._notImpl('subscribeToEvents'); }
}
